<script setup lang="ts">
import { dateTimeFormatter } from '@/helpers/ep/formatters'
import type { LiteSite } from '@/types/core/Site'
import { OrderBy } from '@/types/shared/filters'
import type { VisitorLog } from '@/types/hvl/VisitorLog'
import type { IVisitorLogExportFilters, IVisitorLogFilters } from '@/types/hvl/filters'
import { getAllSites } from '@/services/core.service'
import { deleteVisitorLog, getAllLogStatuses, getAllVisitorLogs, getVisitorLogsExport } from '@/services/hvl.service'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Search } from '@element-plus/icons-vue'
import { Icon } from '@iconify/vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { watchDebounced } from '@vueuse/core'
import { ElMessage } from 'element-plus'
import { DateTime } from 'luxon'
import { computed, onMounted, reactive, ref } from 'vue'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import { downloadFile } from '@/util'
import { NotifyFail } from '@/helpers/core/notifications'

const currentDate = DateTime.now().toJSDate()
const pastMonthDate = DateTime.now().minus({ months: 1 }).toJSDate()
const exportDateFormat = 'yyyy-MM-dd'

const upsertDialogVisible = ref<boolean>(false)
const signOutDialogVisible = ref<boolean>(false)
const siteFilterOptions = ref<LiteSite[]>([])
const statusFilterOptions = ref<string[]>([])
const fragmentFilter = ref<string>()
const dateRangeFilter = ref<[Date, Date]>([pastMonthDate, currentDate])
const visitorLogBeingModified = ref<VisitorLog | null>(null)
const visitorLogBeingSignOut = ref<VisitorLog | null>(null)
const isLoadingExport = ref<boolean>(false)
const showExportTooltip = ref<boolean>(false)
const LogOutStatus = ['Present', 'Delinquent']

watchDebounced(
  fragmentFilter,
  (value) => {
    filters.fragment = value
  },
  { debounce: 1000 }
)

const filters = reactive<IVisitorLogFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'visit_date',
  order_by_order: OrderBy.DESC,
  site_id: undefined,
  visit_date_start_date: pastMonthDate,
  visit_date_end_date: currentDate,
  fragment: undefined,
  status: undefined,
})

// Vue Query
const queryClient = useQueryClient()

const { isFetching, isPending, isError, isRefetching, data, error, refetch } = useQuery({
  queryKey: ['visitor-logs', filters],
  queryFn: () => getAllVisitorLogs(filters),
  placeholderData: (prev: any) => prev,
})

// Compute message to display when table is empty.
const tableEmptyText = computed(() => {
  if (isFetching.value && !filters.fragment) return 'Loading...'
  if (isFetching.value && filters.fragment) return `Searching for ${filters.fragment}`
  if (filters.fragment && !isFetching.value) return `No results for "${filters.fragment}"`
  else return 'No data'
})

/**
 * Handles edit of a visitor log (sets modifying state, opens dialog).
 *
 * @param visitorLog log to edit.
 */
const handleEdit = async (visitorLog: VisitorLog) => {
  visitorLogBeingModified.value = visitorLog
  upsertDialogVisible.value = true
}

/**
 * Handles "sign out" action button click.
 *
 * @param visitorLog the visitor log to sign out.
 */
const handleSignOut = async (visitorLog: VisitorLog) => {
  visitorLogBeingSignOut.value = visitorLog
  signOutDialogVisible.value = true
}

/**
 * Handle `el-datepicker` range change.
 *
 * @param newRange the new selected date range.
 */
const handleDateRangeChange = async (newRange: Array<Date> | null) => {
  if (!newRange) {
    filters.visit_date_start_date = undefined
    filters.visit_date_end_date = undefined
    return
  }

  filters.visit_date_start_date = newRange[0]
  filters.visit_date_end_date = newRange[1]
}

// DELETE mutation
const handleDelete = useMutation({
  mutationFn: (id: number) => {
    return deleteVisitorLog(id)
  },
  onSuccess: () => {
    ElMessage({
      message: `Log deleted successfully.`,
      type: 'success',
    })
    queryClient.invalidateQueries({ queryKey: ['visitor-logs'] })
  },
  onError: (e) => {
    ElMessage({
      message: e.message,
      type: 'error',
    })
  },
})

const handleExport = async () => {
  isLoadingExport.value = true

  // The date filters values will always be available here
  const startDate = DateTime.fromJSDate(filters.visit_date_start_date!).toFormat(exportDateFormat)
  const endDate = DateTime.fromJSDate(filters.visit_date_end_date!).toFormat(exportDateFormat)

  const exportFilters: IVisitorLogExportFilters = {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...filters,
  }

  try {
    const blobFile = await getVisitorLogsExport(exportFilters)
    downloadFile(blobFile, `Visitor Logs Export ${startDate} to ${endDate}.csv`)
  } catch (err: any) {
    ElMessage({
      message: `Failed to export Visitor Logs to file: ${err.message}`,
      type: 'error',
    })
  } finally {
    isLoadingExport.value = false
  }
}

/**
 * isExportDisabled: disables export button if the date filters are empty or if there are no rows to show
 */
const isExportDisabled = computed(() => {
  const dateCondition = !filters.visit_date_start_date || !filters.visit_date_end_date
  const rowsCondition = data.value?.data.length === 0

  return dateCondition || rowsCondition
})

/**
 * On mount: fetch sites; populate sites filter.
 */
onMounted(async () => {
  try {
    const [sitesData, statusesData] = await Promise.all([getAllSites(), getAllLogStatuses()])

    if (sitesData) {
      siteFilterOptions.value = sitesData
    }

    if (statusesData) {
      statusFilterOptions.value = statusesData
    }
  } catch {
    NotifyFail('Failed to fetch resources')
  }
})
</script>

<template>
  <ViewLayout class="visitor-logs">
    <template v-if="!isError" #controls>
      <el-tooltip
        content="Choose a date range with logs"
        placement="bottom"
        :visible="showExportTooltip && isExportDisabled"
      >
        <el-button
          type="primary"
          :loading="isLoadingExport"
          :disabled="isFetching || isExportDisabled"
          @click="handleExport"
          @mouseenter="showExportTooltip = true"
          @mouseleave="showExportTooltip = false"
          >Export</el-button
        >
      </el-tooltip>
      <el-button type="primary" :disabled="isFetching" @click="upsertDialogVisible = true">Create Log</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="visitor-logs__controls">
        <div class="visitor-logs__filters">
          <el-select v-model="filters.site_id" style="width: 232px" placeholder="Site" clearable>
            <el-option v-for="s in siteFilterOptions" :key="s.site_name" :label="s.site_name" :value="s.site_id" />
          </el-select>
          <el-select v-model="filters.status" style="width: 232px" placeholder="Log Status" clearable>
            <el-option v-for="status in statusFilterOptions" :key="status" :label="status" :value="status" />
          </el-select>
          <el-date-picker
            v-model="dateRangeFilter"
            type="daterange"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            :editable="false"
            @change="handleDateRangeChange"
          />
        </div>
        <div>
          <el-input
            v-model="fragmentFilter"
            style="width: 232px"
            placeholder="Search logs"
            clearable
            :suffix-icon="Search"
            :disabled="isPending"
          />
        </div>
      </div>
      <el-table v-loading="isFetching" :data="data?.data" :empty-text="tableEmptyText" stripe>
        <el-table-column prop="site_id" label="Site" :min-width="160" />
        <el-table-column prop="name" label="Name" :min-width="192" />
        <el-table-column prop="company" label="Company" :min-width="192">
          <template #default="scope">
            <template v-if="!scope.row.company">-</template>
            <template v-else>
              {{ scope.row.company }}
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="nature_of_visit" label="Nature of Visit" :min-width="128" />
        <el-table-column
          prop="sign_in"
          :label="`Time In (${DateTime.now().toFormat('ZZZZ')})`"
          :formatter="dateTimeFormatter"
          :min-width="172"
        />
        <el-table-column
          prop="sign_out"
          :label="`Time Out (${DateTime.now().toFormat('ZZZZ')})`"
          :formatter="dateTimeFormatter"
          :min-width="172"
        />
        <el-table-column prop="status" label="Status" :min-width="112">
          <template #default="scope">
            <StatusBadge :status="scope.row.status" />
          </template>
        </el-table-column>
        <el-table-column :min-width="112" align="right">
          <template #default="scope">
            <el-button
              v-if="LogOutStatus.includes(scope.row.status)"
              link
              title="Sign Out"
              @click="handleSignOut(scope.row as VisitorLog)"
              ><Icon icon="material-symbols:logout-rounded"
            /></el-button>
            <el-button link title="Edit" @click="handleEdit(scope.row as VisitorLog)">
              <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
            </el-button>
            <el-button
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Log',
                  'Are you sure you want to delete this log? This action cannot be undone',
                  scope.row.id,
                  handleDelete.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:page-size="filters.per_page"
        v-model:current-page="filters.page"
        class="visitor-logs__pagination"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isFetching"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
      <UpsertVisitorLogDialog
        v-model="upsertDialogVisible"
        :visitor-log="visitorLogBeingModified"
        :sites="siteFilterOptions"
        :default-site="filters.site_id"
        @close="visitorLogBeingModified = null"
      />
      <SignOutVisitorLogDialog
        v-if="signOutDialogVisible"
        v-model="signOutDialogVisible"
        :visitor-log="visitorLogBeingSignOut"
        @close="visitorLogBeingSignOut = null"
      />
    </template>
  </ViewLayout>
</template>

<style scoped lang="scss">
.visitor-logs {
  &__controls {
    display: inline-flex;
    justify-content: space-between;
  }

  &__filters {
    display: flex;
    gap: var(--spacing-spacing-sm);
  }

  &__pagination {
    align-self: flex-end;
  }
}
</style>
